import { useEffect, useState } from "react";
import { Select, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Milestone, ProjectSection, Tag } from "../lib/api.js";
import { TagPicker } from "./TagPicker.js";

/**
 * Where a task sits in its project's structure — section, milestone — and what
 * it is tagged with. Section and milestone belong to a project, so they appear
 * only for a task that has one; tags belong to the office and always show.
 */

interface Props {
  taskId: string;
  projectId: string | null;
  sectionId: string | null;
  milestoneId: string | null;
  tags: Tag[];
  canEdit: boolean;
  /** Save a section or milestone change through the task's own PATCH. */
  onPatch: (fields: { sectionId?: string | null; milestoneId?: string | null }) => void;
  onTagsChanged: (tags: Tag[]) => void;
}

export function TaskStructureFields({ taskId, projectId, sectionId, milestoneId, tags, canEdit, onPatch, onTagsChanged }: Props) {
  const { toast } = useToast();
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);

  useEffect(() => {
    setSections([]);
    setMilestones([]);
    if (!projectId) return;
    let cancelled = false;
    api
      .get<{ sections: ProjectSection[] }>(`/projects/${projectId}/sections`)
      .then((res) => !cancelled && setSections(res.sections))
      .catch(() => undefined);
    api
      .get<{ milestones: Milestone[] }>(`/projects/${projectId}/milestones`)
      .then((res) => !cancelled && setMilestones(res.milestones))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const saveTags = (next: Tag[]) => {
    const previous = tags;
    onTagsChanged(next);
    api
      .put<{ tags: Tag[] }>(`/tasks/${taskId}/tags`, { tagIds: next.map((t) => t.id) })
      .then((res) => onTagsChanged(res.tags))
      .catch((e: unknown) => {
        onTagsChanged(previous);
        toast(e instanceof ApiError ? e.message : "Could not save those tags.", { tone: "danger" });
      });
  };

  return (
    <>
      {projectId && (sections.length > 0 || milestones.length > 0) && (
        <div className="task-detail__field-row">
          {sections.length > 0 && (
            <Select label="Section" value={sectionId ?? ""} onChange={(e) => onPatch({ sectionId: e.target.value || null })}>
              <option value="">No section</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
          {milestones.length > 0 && (
            <Select label="Milestone" value={milestoneId ?? ""} onChange={(e) => onPatch({ milestoneId: e.target.value || null })}>
              <option value="">No milestone</option>
              {milestones.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}

      {(canEdit || tags.length > 0) && (
        <div className="task-detail__section">
          <h3 className="task-detail__section-title">Tags</h3>
          <TagPicker value={tags} onChange={saveTags} disabled={!canEdit} />
        </div>
      )}
    </>
  );
}
